import { useState, useRef, useEffect, useMemo, useCallback, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import { TWclassNames } from "./Div";

// Styling: Promang component kit (PROMANG_COMPONENTS.md §6 Tabs) — `pm-tab`,
// `pm-menu` classes in src/index.css, themed by the global --pls-* tokens.
const DROPDOWN_WIDTH = 248; // px, matches the rendered menu width
const TAB_GAP = 4; // px, gap between tabs in the bar
const VIEWPORT_MARGIN = 8; // px, min distance the dropdown keeps from the viewport edge

// Max number of inline tabs per screen size (everything else goes to the "More"
// menu). Tailwind breakpoints; sorted desc so the first match wins. The
// width-driven measurement still applies on top, so tabs can never overflow.
const DEFAULT_RESPONSIVE_TABS = [
  { min: 1536, count: 10 }, // 2xl
  { min: 1280, count: 8 }, // xl
  { min: 1024, count: 6 }, // lg
  { min: 768, count: 4 }, // md
  { min: 0, count: 3 }, // sm and below (above the mobile <select> breakpoint)
];

const getResponsiveCap = (width, mapping) => {
  const match = mapping.find((bp) => width >= bp.min);
  return match ? match.count : mapping[mapping.length - 1].count;
};

const TabContent = ({ tab }) => (
  <>
    {tab?.icon && <tab.icon className="h-5 w-5 shrink-0" aria-hidden="true" />}
    <span className="whitespace-nowrap">{tab?.name}</span>
  </>
);

export const PMTabs = (props) => {
  const {
    selectedTab,
    setSelectedTab,
    maxVisibleTabs,
    hideMobileSelect = false,
    embedded = false,
  } = props;
  const { t } = useTranslation();
  const moreLabel = props.moreLabel ?? t("common.tabs.more");
  const responsiveTabs = props.responsiveTabs ?? DEFAULT_RESPONSIVE_TABS;

  const [showMoreTabs, setShowMoreTabs] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  const navRef = useRef(null);
  const measureRef = useRef(null);
  const moreButtonRef = useRef(null);

  // Keep original tabs array for correct indexing into project.js `tabs[selectedTab]`.
  const allTabs = props.tabs;
  const activeTabs = useMemo(
    () => allTabs.filter((tab) => tab.active !== false),
    [allTabs]
  );

  // Width-driven count of how many tabs fit. Start optimistic; the measure
  // effect corrects it on mount before paint.
  const [visibleCount, setVisibleCount] = useState(activeTabs.length);

  const recompute = useCallback(() => {
    const nav = navRef.current;
    const measurer = measureRef.current;
    if (!nav || !measurer) return;

    const containerWidth = nav.clientWidth;
    if (containerWidth <= 0) return;

    const nodes = Array.from(measurer.children);
    // Last measured node is the hidden "More" button template.
    const moreWidth = nodes.length ? nodes[nodes.length - 1].getBoundingClientRect().width : 0;
    const tabWidths = nodes.slice(0, activeTabs.length).map((el) => el.getBoundingClientRect().width);

    // Does everything fit without a More button?
    const totalWidth = tabWidths.reduce((sum, w, i) => sum + w + (i > 0 ? TAB_GAP : 0), 0);
    let count;
    if (totalWidth <= containerWidth) {
      count = activeTabs.length;
    } else {
      // Reserve room for the More button and fit as many tabs as possible.
      const available = containerWidth - moreWidth - TAB_GAP;
      let used = 0;
      count = 0;
      for (let i = 0; i < tabWidths.length; i += 1) {
        const next = used + tabWidths[i] + (i > 0 ? TAB_GAP : 0);
        if (next > available) break;
        used = next;
        count += 1;
      }
      // Always keep at least one visible tab when there is any room at all.
      if (count === 0 && tabWidths.length > 0) count = 1;
    }

    // Cap the inline tab count by screen size so wide screens don't cram in
    // every tab; the rest fall into the "More" menu predictably per breakpoint.
    const screenCap = getResponsiveCap(window.innerWidth, responsiveTabs);
    count = Math.min(count, screenCap);

    if (maxVisibleTabs != null) count = Math.min(count, maxVisibleTabs);
    count = Math.min(count, activeTabs.length);

    setVisibleCount((prev) => (prev === count ? prev : count));
  }, [activeTabs.length, maxVisibleTabs, responsiveTabs]);

  // Measure on mount + whenever the tab set changes, before paint to avoid flicker.
  useLayoutEffect(() => {
    recompute();
  }, [recompute]);

  // Recompute on container/viewport resize.
  useEffect(() => {
    const nav = navRef.current;
    if (!nav || typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", recompute);
      return () => window.removeEventListener("resize", recompute);
    }
    const observer = new ResizeObserver(() => recompute());
    observer.observe(nav);
    window.addEventListener("resize", recompute);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", recompute);
    };
  }, [recompute]);

  const visibleTabs = activeTabs.slice(0, visibleCount);
  const moreTabs = activeTabs.slice(visibleCount);

  const selectedTabInMore = moreTabs.some(
    (tab) => allTabs.findIndex((x) => x === tab) === selectedTab
  );
  const selectedTabName = allTabs[selectedTab]?.name;

  // Robust dropdown positioning: stays attached on scroll/resize, clamped to viewport.
  const positionDropdown = useCallback(() => {
    const btn = moreButtonRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const maxLeft = window.innerWidth - DROPDOWN_WIDTH - VIEWPORT_MARGIN;
    const left = Math.max(VIEWPORT_MARGIN, Math.min(rect.right - DROPDOWN_WIDTH, maxLeft));
    const top = Math.min(rect.bottom + 6, window.innerHeight - VIEWPORT_MARGIN);
    setDropdownPosition({ top, left });
  }, []);

  useEffect(() => {
    if (!showMoreTabs) return undefined;
    positionDropdown();
    const onScroll = () => positionDropdown();
    const onKey = (e) => {
      if (e.key === "Escape") setShowMoreTabs(false);
    };
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
      window.removeEventListener("keydown", onKey);
    };
  }, [showMoreTabs, positionDropdown]);

  // Close the dropdown when its contents disappear (e.g. resize moves all tabs inline).
  useEffect(() => {
    if (moreTabs.length === 0 && showMoreTabs) setShowMoreTabs(false);
  }, [moreTabs.length, showMoreTabs]);

  const tabBaseClass = "pm-tab group relative";

  const renderInlineTab = (tab) => {
    const actualIndex = allTabs.findIndex((x) => x === tab);
    const isActive = actualIndex === selectedTab;
    return (
      <button
        key={tab.name}
        type="button"
        onClick={() => setSelectedTab(actualIndex)}
        className={TWclassNames(tabBaseClass, "shrink-0", isActive ? "is-active" : "")}
        aria-current={isActive ? "page" : undefined}
      >
        <TabContent tab={tab} />
      </button>
    );
  };

  return (
    <div className={hideMobileSelect ? "hidden sm:block" : undefined}>
      {!hideMobileSelect ? (
        <div className="sm:hidden">
          <label htmlFor="tabs" className="sr-only">
            {t("Select a tab") || "Select a tab"}
          </label>
          <select
            onChange={(e) => setSelectedTab(e?.target?.value)}
            id="tabs"
            name="tabs"
            className="pm-input"
            value={selectedTab}
          >
            {activeTabs.map((tab) => {
              const actualIndex = allTabs.findIndex((x) => x === tab);
              return (
                <option key={tab?.name} value={actualIndex}>
                  {tab?.name}
                </option>
              );
            })}
          </select>
        </div>
      ) : null}

      <div className={embedded ? "block" : "hidden sm:block"}>
        <div
          className={embedded ? "relative overflow-hidden" : "relative overflow-hidden rounded-xl"}
          style={
            embedded
              ? undefined
              : { background: "var(--pls-surface)", border: "1px solid var(--pls-border)" }
          }
        >
          {/* Hidden measurer: renders every tab + the More button at natural width
              so we can compute how many actually fit the container. */}
          <div
            ref={measureRef}
            aria-hidden="true"
            className="pointer-events-none absolute left-0 top-0 flex opacity-0"
            style={{ visibility: "hidden", gap: TAB_GAP, zIndex: -1 }}
          >
            {activeTabs.map((tab) => (
              <span key={tab.name} className={tabBaseClass}>
                <TabContent tab={tab} />
              </span>
            ))}
            <span className={tabBaseClass}>
              <span className="whitespace-nowrap">{moreLabel}</span>
              <ChevronDownIcon className="h-4 w-4" />
            </span>
          </div>

          <nav
            ref={navRef}
            className="flex"
            style={{
              gap: TAB_GAP,
              borderBottom: embedded ? "none" : "1px solid var(--pls-border)",
              overflow: "hidden",
              height: embedded ? 44 : undefined,
            }}
            aria-label="Tabs"
          >
            {visibleTabs.map(renderInlineTab)}

            {moreTabs.length > 0 && (
              <button
                ref={moreButtonRef}
                type="button"
                onClick={() => setShowMoreTabs((v) => !v)}
                className={TWclassNames(
                  tabBaseClass,
                  "shrink-0 ml-auto",
                  selectedTabInMore ? "is-active" : ""
                )}
                aria-haspopup="menu"
                aria-expanded={showMoreTabs}
              >
                <span className="whitespace-nowrap">
                  {selectedTabInMore && selectedTabName ? selectedTabName : moreLabel}
                </span>
                <ChevronDownIcon
                  className={TWclassNames(
                    "h-4 w-4 transition-transform",
                    showMoreTabs ? "rotate-180" : ""
                  )}
                />
              </button>
            )}
          </nav>
        </div>
      </div>

      {showMoreTabs &&
        createPortal(
          <>
            <div
              className="fixed inset-0 z-40"
              onClick={() => setShowMoreTabs(false)}
            />
            <div
              role="menu"
              className="pm-menu fixed z-50 overflow-hidden"
              style={{
                top: dropdownPosition.top,
                left: dropdownPosition.left,
                width: DROPDOWN_WIDTH,
                maxHeight: `calc(100vh - ${dropdownPosition.top + VIEWPORT_MARGIN}px)`,
                overflowY: "auto",
              }}
            >
              {moreTabs.map((tab) => {
                const actualIndex = allTabs.findIndex((x) => x === tab);
                const isActive = actualIndex === selectedTab;
                return (
                  <button
                    key={tab.name}
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setSelectedTab(actualIndex);
                      setShowMoreTabs(false);
                    }}
                    className={TWclassNames("pm-menu__item", isActive ? "is-active" : "")}
                  >
                    {tab?.icon && <tab.icon className="h-5 w-5 shrink-0" aria-hidden="true" />}
                    <span className="truncate">{tab.name}</span>
                  </button>
                );
              })}
            </div>
          </>,
          document.body
        )}
    </div>
  );
};
